/**
 * Endless mode — per-wave modifier rotation (Sprint 47).
 *
 * Adds a deterministic per-wave **modifier** picked from a five-entry roster
 * to every non-surge, non-boss-milestone Endless wave (`w > campaignLength`).
 * Each modifier reweights the existing seven-enemy spawn pool and optionally
 * nudges the spawn interval — composition only, no new combat behaviour. The
 * rotation queries the post-keystone tower roles (Sniper line-of-fire, Mortar
 * AoE, Arc surplus, Cryo painter, Streak-aware Sniper alignment, Pylon-aura
 * placement, Overclock burst) every wave, replacing the flat-Endless build
 * loop with one where build relevance actually persists past wave 11.
 *
 * Surge (`w % endlessSurgeEvery === 0`) and boss-milestone waves are
 * deliberately **not** rotated — those waves already carry a designed
 * identity (the budget spike, the Kernel) and modifier rotation lives in
 * the four waves between each surge.
 *
 * Determinism: `pickEndlessModifier` derives a per-wave PRNG via
 * `mulberry32((seed ^ Math.imul(w, 0x9E3779B9) ^ MODIFIER_SALT) >>> 0)`, a
 * pure function of `(seed, w)`. The salt decorrelates this pick from
 * `generateWave`'s own per-wave RNG (`seed ^ w * 0x9E3779B9`) so the
 * weighted-spawn sequence on no-modifier (surge / boss) waves is byte-
 * identical to pre-S47. The save schema is unchanged — the modifier id is
 * derived from `(seed, wave)`, not persisted. See docs/ENDLESS_DESIGN.md.
 */

import type { EnemyType } from '../config';
import { CONFIG } from '../config';
import { mulberry32 } from '../engine/util';

/** Stable id for a per-wave Endless modifier. */
export type EndlessModifierId =
  | 'armoredSurge'
  | 'fastPack'
  | 'scatteredSpread'
  | 'streakRunners'
  | 'daemonRush';

/**
 * One modifier — a partial weight-multiplier table over the seven enemy types
 * (1.0 = unchanged) and a signed interval delta (added to the base interval
 * after the surge boost). `intervalDelta` of `-2` means spawns come 2 ticks
 * faster; `+2` means 2 ticks slower. The base interval is clamped to [3, 8]
 * pre-S47; the post-modifier interval is re-clamped to the same range.
 *
 * `label` is the player-facing string shown in the build-phase preview
 * (rendered by `src/ui/hud.ts`).
 */
export interface EndlessModifier {
  readonly id: EndlessModifierId;
  readonly label: string;
  readonly weightMult: Readonly<Partial<Record<EnemyType, number>>>;
  readonly intervalDelta: number;
}

/**
 * The five-entry modifier roster. Ordering is stable — `pickEndlessModifier`
 * indexes into this list, so re-ordering would shift every deterministic
 * pick (do not re-order without a re-validation pass on the s47 harness).
 *
 * Each modifier is engineered against a specific keystone role:
 *  - `armoredSurge` → Sniper line-of-fire + Cryo brittle
 *  - `fastPack` → Mortar AoE + Cryo painter chokepoint
 *  - `scatteredSpread` → Arc surplus chain + Pylon-aura placement
 *  - `streakRunners` → Sniper alignment vs Streak proximity-sprint
 *  - `daemonRush` → Overclock burst window + Pulse pile + brittle
 *
 * See docs/ENDLESS_DESIGN.md §2.1 for the full rationale per modifier.
 */
export const ENDLESS_MODIFIERS: readonly EndlessModifier[] = [
  {
    id: 'armoredSurge',
    label: 'Armoured Surge',
    weightMult: { cipher: 2.5, buffer: 2.5, bit: 0.3 },
    intervalDelta: 0,
  },
  {
    id: 'fastPack',
    label: 'Fast Pack',
    // Sprint 51 polish (polish-s51, docs/PLAYTEST_ENDLESS.md §5.1 Option A):
    // bit ×3.0 → ×2.5, packet ×2.0 → ×1.5 — softens the rotation's only
    // "easy row" (the playtest matrix had it as every build's lowest leak,
    // ~5.0 on sniperLines, ~9.0 on overclock). The composition still leans
    // bit/packet hard, but the Pulse / Sniper / Overclock-burst answer is no
    // longer a give-away kill-table. Interval delta `-2` is kept so the
    // "Fast Pack is the quick wave" mental model still reads.
    weightMult: {
      bit: 2.5,
      packet: 1.5,
      fork: 0.5,
      daemon: 0.5,
      cipher: 0.5,
      patch: 0.5,
      buffer: 0.5,
    },
    intervalDelta: -2,
  },
  {
    id: 'scatteredSpread',
    label: 'Scattered Spread',
    weightMult: { fork: 2.5, packet: 1.5, daemon: 0.5, bit: 0.7 },
    intervalDelta: +2,
  },
  {
    id: 'streakRunners',
    label: 'Streak Runners',
    weightMult: { streak: 4.0, bit: 0.5, packet: 0.7 },
    intervalDelta: 0,
  },
  {
    id: 'daemonRush',
    label: 'Daemon Rush',
    weightMult: { daemon: 3.0, buffer: 1.5, bit: 0.5, packet: 0.5 },
    intervalDelta: -1,
  },
];

/**
 * Decorrelates the modifier-pick PRNG from `generateWave`'s own per-wave
 * RNG. Picked from the same family as the wave-seed mixer
 * (`Math.imul(w, 0x9E3779B9)`) — a fixed 32-bit odd salt with reasonable
 * bit distribution. Changing this constant is a save-incompatible deterministic
 * shift (the modifier rotation across every existing seed will reshuffle).
 */
const MODIFIER_SALT = 0xe5d71377;

/**
 * True if this Endless wave is in the **modifier-rotation** window — i.e.
 * past the campaign table AND not a surge wave (every 5th wave by
 * `endlessSurgeEvery`). Boss-milestone waves (20, 30, …) are already surge
 * waves (all divisible by 5) so they fall out naturally. Modifier rotation
 * lives in the four waves between each surge.
 */
export function isEndlessModifierWave(w: number): boolean {
  if (w <= CONFIG.waves.length) return false;
  if (w % CONFIG.endlessSurgeEvery === 0) return false;
  return true;
}

/**
 * The deterministic per-wave Endless modifier — `null` on campaign waves,
 * surge waves, and boss-milestone waves; one of `ENDLESS_MODIFIERS` otherwise.
 * Pure function of `(seed, w)`.
 */
export function pickEndlessModifier(seed: number, w: number): EndlessModifier | null {
  if (!isEndlessModifierWave(w)) return null;
  const mixed = (seed ^ Math.imul(w, 0x9e3779b9) ^ MODIFIER_SALT) >>> 0;
  const r = mulberry32(mixed);
  const idx = Math.floor(r * ENDLESS_MODIFIERS.length) % ENDLESS_MODIFIERS.length;
  return ENDLESS_MODIFIERS[idx]!;
}

/**
 * Apply a modifier in-place to a weights table and return the adjusted
 * spawn interval. Multiplies each declared weight key by `weightMult[k]`,
 * leaves other keys untouched, then adds `intervalDelta` to `baseInterval`
 * and re-clamps to `[3, 8]` (the same range `generateWave` uses).
 *
 * The caller (`generateWave` in `systems.ts`) passes the already-surge-
 * boosted weights and the already-clamped base interval, so modifiers
 * compose cleanly *after* the surge boost. Modifier waves are guaranteed
 * to be non-surge (see `isEndlessModifierWave`) — but the compose order is
 * documented for completeness and to make future surge-aware modifiers
 * trivially safe to add.
 */
export function applyEndlessModifier(
  weights: Record<EnemyType, number>,
  baseInterval: number,
  modifier: EndlessModifier,
): { weights: Record<EnemyType, number>; interval: number } {
  for (const [type, mult] of Object.entries(modifier.weightMult)) {
    if (mult === undefined) continue;
    const key = type as EnemyType;
    weights[key] = Math.max(0, weights[key] * mult);
  }
  const interval = Math.max(3, Math.min(8, baseInterval + modifier.intervalDelta));
  return { weights, interval };
}
